import {ISolution, SolutionType} from "./SolutionDataSource";

declare var $:any;

import {FeedItemEntity, initializeFeedItemEntities} from "data/entities/FeedItemEntity";
import {BundleData} from "data/BundleData";
import {MainStageData} from "data/MainStageData";
import {UIState} from "data/UIState";
import * as moment from "moment";
import {GET_QUERY} from "utils";
import {PointsDataSource} from "data/PointsDataSource";
import {action, computed, observable} from "mobx";

/**
 * Data source for the selected Solution,
 * feed items, points, ads, chartable posts
 *
 */
export class FeedData {

  solution:ISolution;
  pointsDataSource:PointsDataSource;
  @observable feed:FeedItemEntity[] = [];
  @observable ads:Array<any> =[];
  @observable chartablePosts:Array<any> =[];

  @observable lastRefresh:moment.Moment;

  @observable loadingFeed = false;
  @observable feedLoaded = false;
  @observable loadFailed = false;
  @observable checkingForNewFeedItems:boolean = false;

  @observable badgeEarnedAck = true;
  @computed get showBadgeEarnedConfetti() {
    if(this.pointsDataSource && this.solution.badgeUsed && this.solution.badgeThreshold) {
      return false===this.badgeEarnedAck && this.pointsDataSource.points >= this.solution.badgeThreshold;
    }
    return false;
  }

  set showBadgeEarnedConfetti(show:boolean) {
    this.badgeEarnedAck = !show;
  }

  updatesTimer = () => {
    if (1 <= moment().diff(this.lastRefresh, 'minutes')) {

      const runUpdatesTimerAgain = () => {
        setTimeout(() => {
          this.updatesTimer()
        }, 1 * 60 * 1000);
      };

      this.checkForNewFeedItems()
        .then(() => {
          this.lastRefresh = moment();
          runUpdatesTimerAgain();
        })
        .catch(() => {
          runUpdatesTimerAgain();
        });
    }
  }

  private checkForNewFeedItems = () => new Promise<void>((resolve)=> {
    // ignore if a check is already in progress
    if (!this.checkingForNewFeedItems) {
      let newerThanTimestamp = this.feed && this.feed.length ? this.feed[0].displayDate : moment();
      if (null != newerThanTimestamp) {
        this.checkingForNewFeedItems = true;
        this.getFeed(newerThanTimestamp, null)
          .then((feed: any) => {
            if (feed.items && feed.items.length > 0) {
              this.pointsDataSource.processFeedItems(feed.items);
              const entities = initializeFeedItemEntities(feed.items);
              for (let i = entities.length - 1; i > -1; i--) {
                this.feed.unshift(entities[i]);
              }
            }
            this.checkingForNewFeedItems = false;
            resolve();
          })
          .catch(() => {
            this.checkingForNewFeedItems = false;
            resolve();
          });
      }
    } else {
      resolve();
    }
  });

  @action addFeedItem = (feedItem) => {
    this.pointsDataSource.processFeedItems([feedItem]);
    const entities = initializeFeedItemEntities([feedItem]);
    for (let i = entities.length - 1; i > -1; i--) {
      this.feed.unshift(entities[i]);
    }
  }

  getFeed = (newerThanTimestamp, olderThanTimestamp) => new Promise((resolve, reject) => {
    let url = '/feed?solutionId='+this.solution._id;
    if(null!=newerThanTimestamp){
      url += "&newerThanTimestamp="+newerThanTimestamp;
    } else if(null!=olderThanTimestamp) {
      url += "&olderThanTimestamp="+olderThanTimestamp;
    }
    $.ajax(GET_QUERY(url))
      .done(response=>{
        resolve(response);
      })
      .fail((jqXHR, textStatus, errorThrown)=> {
        reject(jqXHR);
      });
  });

  /* just update comments, not points*/
  @action updatePostCommentsInBrowser = (feedItem) => {
    for(let i=0;i<this.feed.length;i++) {
      if (this.feed[i]._id === feedItem._id) {
        this.feed[i]._post.comments = feedItem._post.comments;
        break;
      }
    }
  }


  @action updateFeedItemInBrowser = (feedItem) => {
    // find this feed item and update it
    for(let i=0;i<this.feed.length;i++){
      if(this.feed[i]._id===feedItem._id){
        this.feed[i].step = feedItem.step;
        this.feed[i].data = feedItem.data;
        if(feedItem.pointsAwarded && feedItem.pointsAwarded>0) {
          this.feed[i].pointsAwarded = feedItem.pointsAwarded;
          this.feed[i].pointsAwardedDate = feedItem.pointsAwardedDate;
          this.pointsDataSource.processFeedItem(feedItem);
        }
        this.feed[i].read = feedItem.read;
        this.feed[i]._post.comments = feedItem._post.comments;
        break;
      }
    }
  }

  /**
   * Create a FeedData object and initialize all of its components
   * @param solution
   */
  static feedDataFactory = (solution:ISolution) => new Promise((resolve, reject) => {
    const feedData = new FeedData(solution);
    feedData.feedLoaded = false;
    feedData.loadFailed = false;
    feedData.lastRefresh = moment();

    feedData.getFeed(null, null)
      .then((feed:any)=>{
          feedData.ads = feed.ads;
          feedData.chartablePosts = feed.chartablePosts;
          feedData.feed = initializeFeedItemEntities(feed.items);

          // Instantiate the points data source
          PointsDataSource.pointsDataSourceFactory(solution, feed.items)
          .then((pointsDataSource)=>{

            // prep for badge earned confetti
            if(solution.badgeUsed && solution.badgeThreshold && pointsDataSource.points < solution.badgeThreshold) {
              feedData.badgeEarnedAck = false;
            }
            feedData.pointsDataSource = pointsDataSource;

            BundleData.clear();
            MainStageData.clear();
            BundleData.load(feed.bundles);

            // Now complete the initialization
            if(feedData.solution.type===SolutionType.HEALTH_FAIR) {
              MainStageData.load(feedData.solution, feed.mainStageEvents);
              UIState.setHealthFairHost(feed.host);
            }

            feedData.feedLoaded = true;
            feedData.loadingFeed = false;

            // start the timer
            feedData.updatesTimer();
            resolve(feedData);

          });
      })
      .catch(()=>{
        feedData.loadFailed = true;
        feedData.loadingFeed = false;
        console.log('failed to load feed')
        reject('Failed to load feed.');
      });
  });

  private constructor(solution:ISolution) {
    this.solution = solution;
  }

}


